import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import resultService, { EnhancedResult, ResultFilters } from "../../services/resultService";
import { AssessmentTemplate } from "../../types";
import { fmtDate } from "../../utils/helpers";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Modal from "../../components/ui/Modal";
import Badge from "../../components/ui/Badge";
import ProgressBar from "../../components/ui/ProgressBar";
import Button from "../../components/ui/Button";

const ResultsPage: React.FC<{ candidateId?: string; examId?: string; assignmentId?: string }> = ({ 
  candidateId, 
  examId, 
  assignmentId 
}) => {
  const { currentUser, traits } = useApp();
  const [selected, setSelected] = useState<EnhancedResult | null>(null);
  const [results, setResults] = useState<EnhancedResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    averageScore: 0,
    passedCount: 0,
    highScoreCount: 0,
    lowScoreCount: 0,
    passRate: 0,
    averageTimeSpent: 0,
    topPerformers: [] as EnhancedResult[],
    recentResults: [] as EnhancedResult[]
  });
  const [availableExams, setAvailableExams] = useState<AssessmentTemplate[]>([]);
  const [filters, setFilters] = useState<ResultFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const queryFilters: ResultFilters = {};
      if (candidateId) queryFilters.candidateId = candidateId;
      if (examId) queryFilters.examId = examId;
      if (assignmentId) queryFilters.assignmentId = assignmentId;
      if (filters.examId) queryFilters.examId = filters.examId;
      if (filters.fromDate) queryFilters.fromDate = filters.fromDate;
      if (filters.toDate) queryFilters.toDate = filters.toDate;
      if (filters.minScore !== undefined) queryFilters.minScore = filters.minScore;
      if (filters.maxScore !== undefined) queryFilters.maxScore = filters.maxScore;
      
      const [fetchedResults, fetchedStats, fetchedExams] = await Promise.all([
        resultService.getResultsByRole(currentUser, queryFilters),
        resultService.getResultStatistics(currentUser, queryFilters),
        resultService.getAvailableExams()
      ]);
      
      setResults(fetchedResults);
      setStats(fetchedStats);
      setAvailableExams(fetchedExams);
    } catch (error) {
      console.error("Error loading results:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, candidateId, examId, assignmentId, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getScoreColor = (percentage: number) => {
    if (percentage >= 70) return "#10b981";
    if (percentage >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const getScoreLevel = (percentage: number) => {
    if (percentage >= 70) return "High";
    if (percentage >= 50) return "Moderate";
    return "Low";
  };

  const getTraitLevelColor = (level: string) => {
    switch(level) {
      case "High": return "#10b981";
      case "Moderate": return "#f59e0b";
      default: return "#ef4444";
    }
  };

  const handleFilterChange = (key: keyof ResultFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setShowFilters(false);
  };

  const handleExportCSV = async () => {
    if (!currentUser) return;
    
    const queryFilters: ResultFilters = {};
    if (candidateId) queryFilters.candidateId = candidateId;
    if (examId) queryFilters.examId = examId;
    if (filters.examId) queryFilters.examId = filters.examId;
    
    const csv = await resultService.downloadResultsCSV(currentUser, queryFilters);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `results_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const role = currentUser?.role;

  if (loading) {
    return (
      <PageShell title="Results" subtitle="Loading your assessment results...">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <GlassCard key={i} className="p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </GlassCard>
          ))}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell 
      title={candidateId ? "Candidate Results" : role === 'candidate' ? "My Results" : "Results Dashboard"} 
      subtitle="Exam results and performance analytics"
      action={results.length > 0 ? { label: "Export CSV", onClick: handleExportCSV } : undefined}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Results" value={stats.total} icon="📊" color="#6366f1" />
        <StatCard label="Average Score" value={`${stats.averageScore}%`} icon="📈" color="#10b981" />
        <StatCard label="Passed (≥60%)" value={stats.passedCount} icon="✅" color="#f59e0b" />
        <StatCard label="Pass Rate" value={`${stats.passRate}%`} icon="🏆" color="#ec4899" />
      </div>

      {/* Filters Toggle */}
      {(role === 'super_admin' || role === 'org_admin') && (
        <div className="mb-4">
          <Button variant="secondary" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? "Hide Filters 🔽" : "Show Filters 🔼"}
          </Button>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (role === 'super_admin' || role === 'org_admin') && (
        <GlassCard className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Exam</label>
              <select 
                value={filters.examId || ''} 
                onChange={(e) => handleFilterChange('examId', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Exams</option>
                {availableExams.map(exam => (
                  <option key={exam.id} value={exam.id}>{exam.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Min Score (%)</label>
              <input 
                type="number" 
                value={filters.minScore || ''} 
                onChange={(e) => handleFilterChange('minScore', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="0"
                min="0"
                max="100"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Max Score (%)</label>
              <input 
                type="number" 
                value={filters.maxScore || ''} 
                onChange={(e) => handleFilterChange('maxScore', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="100"
                min="0"
                max="100"
              />
            </div>
            
            <div className="flex items-end">
              <Button variant="secondary" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Results Grid */}
      {results.length === 0 ? (
        <GlassCard className="p-16 text-center">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-gray-500 font-semibold text-lg">No results found</p>
          <p className="text-gray-400 text-sm mt-2">Complete an exam to see your results here</p>
        </GlassCard>
      ) : (
        <>
          {/* Top Performers Section for Admins */}
          {(role === 'super_admin' || role === 'org_admin') && stats.topPerformers.length > 0 && (
            <div className="mb-8">
              <h3 className="font-extrabold text-gray-700 text-sm uppercase tracking-wider mb-4">
                🏆 Top Performers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.topPerformers.map(performer => (
                  <GlassCard key={performer.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-800">{performer.candidateName}</p>
                        <p className="text-xs text-gray-500">{performer.examName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-extrabold text-green-600">{performer.percentage}%</p>
                        <p className="text-xs text-gray-400">{fmtDate(performer.completedAt)}</p>
                      </div>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          )}

          {/* All Results */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {results.map(result => {
              const color = getScoreColor(result.percentage);
              const level = getScoreLevel(result.percentage);
              
              return (
                <GlassCard key={result.id} hover className="p-5 cursor-pointer transition-all hover:shadow-xl" onClick={() => setSelected(result)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-extrabold text-gray-800 truncate">{result.examName || result.examId}</h3>
                        <Badge text={level} />
                      </div>
                      {!candidateId && role !== 'candidate' && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          {result.candidateName} 
                          {result.candidateEmail && <span className="text-xs text-gray-400 ml-1">({result.candidateEmail})</span>}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Completed {fmtDate(result.completedAt)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-3xl font-extrabold" style={{ color }}>{result.percentage}%</p>
                      <p className="text-xs text-gray-400">{result.totalScore}/{result.maxScore} pts</p>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <ProgressBar value={result.percentage} max={100} color={color} height={8} />
                  </div>
                  
                  {result.traitScores && result.traitScores.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {result.traitScores.slice(0, 3).map(ts => (
                        <span key={ts.traitId} className="text-xs px-2.5 py-0.5 rounded-full font-semibold"
                          style={{ background: `${getTraitLevelColor(ts.level)}18`, color: getTraitLevelColor(ts.level) }}>
                          {ts.traitName}: {ts.level}
                        </span>
                      ))}
                      {result.traitScores.length > 3 && (
                        <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          +{result.traitScores.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </GlassCard>
              );
            })}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Detailed Result" width="max-w-3xl">
        {selected && (
          <div className="space-y-6 max-h-[80vh] overflow-y-auto px-1">
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-3xl shadow">
                {selected.percentage >= 70 ? "🎉" : selected.percentage >= 50 ? "📈" : "📚"}
              </div>
              <div className="flex-1">
                <h3 className="font-extrabold text-gray-800 text-lg">{selected.examName || selected.examId}</h3>
                {(!candidateId && role !== 'candidate') && (
                  <>
                    <p className="text-sm text-gray-700 font-medium">{selected.candidateName}</p>
                    {selected.candidateEmail && <p className="text-xs text-gray-500">{selected.candidateEmail}</p>}
                  </>
                )}
                <p className="text-xs text-gray-400 mt-0.5">Completed {fmtDate(selected.completedAt)}</p>
                {selected.assignmentStatus && <Badge text={selected.assignmentStatus} className="mt-1" />}
              </div>
              <div className="text-right">
                <p className="text-4xl font-extrabold" style={{ color: getScoreColor(selected.percentage) }}>
                  {selected.percentage}%
                </p>
                <p className="text-sm text-gray-500">{selected.totalScore} / {selected.maxScore} pts</p>
              </div>
            </div>
            
            <ProgressBar value={selected.percentage} max={100} color={getScoreColor(selected.percentage)} height={12} />
            
            {selected.traitScores && selected.traitScores.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-xl">🧠</span> Psychometric Trait Analysis
                </h4>
                <div className="space-y-4">
                  {selected.traitScores.map(ts => {
                    const trait = traits.find(t => t.id === ts.traitId);
                    const traitColor = getTraitLevelColor(ts.level);
                    return (
                      <div key={ts.traitId} className="p-4 rounded-2xl bg-white/50 border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{ "🧠"}</span>
                            <span className="font-bold text-gray-800">{ts.traitName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-gray-800">{ts.percentage}%</span>
                            <Badge text={ts.level} />
                          </div>
                        </div>
                        <ProgressBar value={ts.score} max={ts.maxScore} color={traitColor} height={10} />
                        <p className="text-xs text-gray-400 mt-1">{ts.score} / {ts.maxScore} points</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {selected.sectionScores && selected.sectionScores.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-xl">📐</span> Section-wise Performance
                </h4>
                <div className="space-y-3">
                  {selected.sectionScores.map(ss => {
                    const c = getScoreColor(ss.percentage);
                    return (
                      <div key={ss.sectionId} className="p-3 rounded-xl bg-gray-50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-gray-700">{ss.sectionName}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-gray-800">{ss.percentage}%</span>
                            <Badge text={getScoreLevel(ss.percentage)} />
                          </div>
                        </div>
                        <ProgressBar value={ss.score} max={ss.maxScore} color={c} height={9} />
                        <p className="text-xs text-gray-400 mt-0.5">{ss.score} / {ss.maxScore} points</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
              <Button onClick={() => window.print()}>Print Report 🖨️</Button>
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
};

export default ResultsPage;